import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { ProductCard } from "../components/ProductCard";
import { StatusBadge } from "../components/StatusBadge";
import { fmtPrice } from "../lib/format";
import { resolveImg } from "../lib/api";
import api from "../lib/api";

export default function Home() {
  const { t, loc, lang, money } = useLang();
  const [products, setProducts] = useState([]);
  const [content, setContent] = useState({});

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const featured = products.find((p) => p.featured) || products[0];

  const headline = content[`hero_headline_${lang}`] || t("brand");
  const sub = content[`hero_sub_${lang}`] || "";
  const lines = (content[`hero_lines_${lang}`] || "").split("\n").map((l) => l.trim()).filter(Boolean);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-center pb-24 md:pb-0 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B0A08]" />
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          style={{ background: "radial-gradient(115% 85% at 18% 22%, rgba(196,154,108,0.20), rgba(11,10,8,0) 62%)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A08] via-transparent to-[#0B0A08]/40" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <p className="text-xs uppercase tracking-[0.35em] text-[#C49A6C] mb-6">{t("house_line")}</p>
            {headline && <h1 className="serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#F2EBE1]">{headline}</h1>}
            <p className="text-[#C9C0B4] text-base md:text-lg leading-relaxed mt-8 max-w-xl">{sub}</p>
            {lines.length > 0 && (
              <div className="mt-8 space-y-1.5">
                {lines.map((l, i) => (
                  <p key={l} className="serif text-xl md:text-2xl text-[#F2EBE1] leading-snug">{l}</p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" data-testid="hero-shop-btn" className="bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] px-9 py-4 hover:bg-[#E7CBA9] transition-colors">
                {t("shop_fragrances")}
              </Link>
              <Link to="/story" data-testid="hero-discover-btn" className="border border-[#8A857D]/40 text-[#F2EBE1] text-xs uppercase tracking-[0.25em] px-9 py-4 hover:border-[#C49A6C] hover:text-[#C49A6C] transition-colors">
                {t("story_of_omr")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Link to={`/product/${featured.slug}`} className="block relative group overflow-hidden bg-[#141311]">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={resolveImg(featured.images?.[0])} alt={loc(featured, "name")} className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105" />
              </div>
            </Link>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#8A857D] mb-5">{t("featured")}</p>
              <div className="mb-4">
                <StatusBadge status={featured.status} />
              </div>
              <h2 className="serif text-4xl md:text-5xl text-[#F2EBE1] leading-tight">{loc(featured, "name")}</h2>
              <p className="text-xs uppercase tracking-[0.18em] text-[#C49A6C] mt-4">
                {featured.size} · {featured.parfum_type} · {featured.concentration}
              </p>
              <p className="text-[#8A857D] leading-relaxed mt-8 max-w-lg">{loc(featured, "description")}</p>
              <div className="flex flex-wrap gap-2 mt-8">
                {(lang === "ar" ? featured.notes_ar : featured.notes_en)?.map((n, i) => (
                  <span key={n} className="text-[11px] uppercase tracking-[0.15em] text-[#8A857D] border border-[#262421] px-3 py-1.5">{n}</span>
                ))}
              </div>
              <div className="flex items-center gap-8 mt-10">
                <span className="serif text-2xl text-[#C49A6C]">{money(featured.price)}</span>
                <Link to={`/product/${featured.slug}`} data-testid="featured-view-btn" className="text-xs uppercase tracking-[0.2em] text-[#F2EBE1] link-underline">
                  {t("view_product")} →
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* COLLECTION preview */}
      {products.length > 1 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
          <div className="flex items-end justify-between mb-12">
            <h2 className="serif text-3xl md:text-4xl">{t("shop_title")}</h2>
            <Link to="/shop" className="text-xs uppercase tracking-[0.2em] text-[#8A857D] hover:text-[#C49A6C] transition-colors link-underline">{t("nav_shop")} →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
